import React from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import { formatCurrency } from '~libs/dataUtils';

import { Spinner } from '~components';
import Icon, { faDollarSign, faPeopleGroup } from '~components/icon';
import SummaryStatGroupCard from '~components/SummaryStatGroupCard';

import { ReconClient } from '~support-at-home/models';

import { useGetReconciliationQuery } from '~support-at-home/actions/reconciliations';

import ClientCard from './ClientCard';

interface ComponentProps {
  value: {
    reconciliationId: string;
  };
}

const Resolve: React.FC<ComponentProps> = ({ value }) => {
  const { reconciliationId } = value;

  const { data, isLoading, isError, error } = useGetReconciliationQuery(
    { id: reconciliationId },
    { skip: !reconciliationId },
  );

  if (!reconciliationId) {
    return (
      <div className="alert alert-warning">
        No reconciliation ID found. Please return to Step 2 and complete file processing.
      </div>
    );
  }

  if (isLoading) {
    return <Spinner loading className="spinner-container" />;
  }

  if (isError) {
    return (
      <div className="alert alert-danger">
        <b>Error Loading Data</b>
        <div>{getErrorMessage(error)}</div>
      </div>
    );
  }

  const { breakdown = [], summary = {} } = data || {};

  return (
    <div className="resolve-wrapper">
      {/* Summary row */}
      <SummaryStatGroupCard
        title="Payment Summary Overview"
        icon={<Icon icon={faDollarSign} />}
        stats={[
          {
            value: formatCurrency(summary?.total_paid),
            label: 'Total Paid',
          },
          {
            value: formatCurrency(summary?.total_gov),
            label: 'Government Contribution',
            className: 'text-primary',
          },
          {
            value: formatCurrency(summary?.total_co_contrib),
            label: 'Co-Contributions',
            className: 'text-success',
          },
          {
            value: summary?.total_records?.toLocaleString() ?? '0',
            label: 'Total Records',
          },
        ]}
      />

      {/* Client Payment Breakdown */}
      <div className="summary-card p-3 border rounded bg-white shadow-sm mt-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center">
            <div className="me-2 text-secondary">
              <Icon icon={faPeopleGroup} />
            </div>
            <div className="fw-semibold">Client Payment Breakdown</div>
          </div>

          <div className="text-muted small">Showing {breakdown.length} clients</div>
        </div>

        <div className="mt-4">
          {breakdown.map((client: ReconClient) => (
            <ClientCard key={client.client_id} client={client} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Resolve;
